/**
 * API Client Functions
 * Centralized API calls for React Query
 */

// Detect if running in Capacitor (native mobile app)
const isCapacitor = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform();

// In production web, use relative URLs (same origin).
// In Capacitor (mobile), use the full production backend URL.
// In development, use localhost.
const API_BASE = isCapacitor
  ? (process.env.REACT_APP_API_URL || 'https://your-backend-domain.com')
  : process.env.NODE_ENV === 'production' 
    ? '' 
    : (process.env.REACT_APP_API_URL || 'http://localhost:4001');

// Store for Clerk user info (set by ClerkUserProvider)
let clerkUserInfo = null;

// Store for current organization (set by OrganizationProvider)
let currentOrganizationId = null;

/**
 * Set Clerk user info for API calls
 * Called by ClerkUserProvider when user logs in
 */
export function setClerkUser(user) {
  clerkUserInfo = user;
}

/**
 * Get current Clerk user info
 */
export function getClerkUser() {
  return clerkUserInfo;
}

/**
 * Set current organization for API calls
 * Called by OrganizationProvider when organization is selected
 */
export function setCurrentOrganization(orgId) {
  currentOrganizationId = orgId;
  console.log('[API] Organization set:', orgId);
}

/**
 * Get current organization ID
 */
export function getCurrentOrganization() {
  return currentOrganizationId;
}

/**
 * Get auth headers for API calls
 * Can be used by components that need to make direct fetch calls
 */
export function getAuthHeaders() {
  const headers = {};
  
  if (clerkUserInfo?.id) {
    headers['X-Clerk-User-Id'] = clerkUserInfo.id;
  }
  if (clerkUserInfo?.email) {
    headers['X-Clerk-User-Email'] = clerkUserInfo.email;
  }
  if (currentOrganizationId) {
    headers['X-Organization-Id'] = currentOrganizationId;
  }
  
  return headers;
}

/**
 * Wrapped fetch function that includes auth headers
 * Use this instead of raw fetch() for API calls
 */
export async function fetchWithAuth(url, options = {}) {
  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...options.headers,
  };
  
  return fetch(fullUrl, {
    ...options,
    headers,
  });
}

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  
  // Build headers with Clerk info if available
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add Clerk user ID if available
  if (clerkUserInfo?.id) {
    headers['X-Clerk-User-Id'] = clerkUserInfo.id;
  }
  if (clerkUserInfo?.email) {
    headers['X-Clerk-User-Email'] = clerkUserInfo.email;
  }
  
  // Add organization ID if available
  if (currentOrganizationId) {
    headers['X-Organization-Id'] = currentOrganizationId;
  }
  
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    const errorMessage = error.error || error.message || `HTTP ${response.status}`;

    // For validation errors, show the first detail message if available
    let finalMessage = errorMessage;
    if (error.details && Array.isArray(error.details) && error.details.length > 0) {
      finalMessage = error.details[0].message || errorMessage;
    }

    const errorWithDetails = new Error(finalMessage);
    errorWithDetails.response = { data: error };
    errorWithDetails.code = error.code;
    throw errorWithDetails;
  }

  return response.json();
}

// Employees API
export const employeesApi = {
  getAll: (active) => apiCall(`/api/employees${active !== undefined ? `?active=${active}` : ''}`),
  getById: (id) => apiCall(`/api/employees/${id}`),
  create: (data) => apiCall('/api/employees', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/employees/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/employees/${id}`, { method: 'DELETE' }),
  toggleActive: (id) => apiCall(`/api/employees/${id}/toggle-active`, { method: 'PATCH' }),
};

// Positions API
export const positionsApi = {
  getAll: () => apiCall('/api/positions'),
  create: (data) => apiCall('/api/positions', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/positions/${id}`, { method: 'DELETE' }),
};

// Shifts API
export const shiftsApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/shifts${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/api/shifts', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/shifts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/shifts/${id}`, { method: 'DELETE' }),
};

// Salaries API
export const salariesApi = {
  getWeekly: (week) => apiCall(`/api/salaries${week ? `?week=${week}` : ''}`),
};

// Employee Payments API
export const employeePaymentsApi = {
  getByEmployee: (employeeId) => apiCall(`/api/employee-payments/employee/${employeeId}`),
  getStats: (employeeId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/employee-payments/employee/${employeeId}/stats${query ? `?${query}` : ''}`);
  },
  getCumulative: (weekStart) => apiCall(`/api/employee-payments/cumulative/${weekStart}`),
  getUnpaid: () => apiCall('/api/employee-payments/stats/unpaid'),
  create: (data) => apiCall('/api/employee-payments', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/employee-payments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/employee-payments/${id}`, { method: 'DELETE' }),
};

// Revenue API
export const revenueApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/revenue${query ? `?${query}` : ''}`);
  },
  getStats: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/revenue/summary/stats${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/api/revenue', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/revenue/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/revenue/${id}`, { method: 'DELETE' }),
};

// Expenses API
export const expensesApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/general-expenses${query ? `?${query}` : ''}`);
  },
  getStats: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/general-expenses/summary/stats${query ? `?${query}` : ''}`);
  },
  create: (data) => apiCall('/api/general-expenses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/general-expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/general-expenses/${id}`, { method: 'DELETE' }),
};

// Categories API
export const categoriesApi = {
  getAll: () => apiCall('/api/expense-categories'),
  getById: (id) => apiCall(`/api/expense-categories/${id}`),
  create: (data) => apiCall('/api/expense-categories', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/expense-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/expense-categories/${id}`, { method: 'DELETE' }),
};

// Supplier Categories API
export const supplierCategoriesApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/supplier-categories${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiCall(`/api/supplier-categories/${id}`),
  create: (data) => apiCall('/api/supplier-categories', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/supplier-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/supplier-categories/${id}`, { method: 'DELETE' }),
};

// Suppliers API
export const suppliersApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/suppliers${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiCall(`/api/suppliers/${id}`),
  create: (data) => apiCall('/api/suppliers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/suppliers/${id}`, { method: 'DELETE' }),
  deleteMany: (ids) => apiCall('/api/suppliers/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
};

// Supplier Invoices API
export const supplierInvoicesApi = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/supplier-invoices${query ? `?${query}` : ''}`);
  },
  getStats: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/api/supplier-invoices/summary/stats${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiCall(`/api/supplier-invoices/${id}`),
  create: (data) => apiCall('/api/supplier-invoices', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/supplier-invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/supplier-invoices/${id}`, { method: 'DELETE' }),
  deleteMany: (ids) => apiCall('/api/supplier-invoices/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  markPaid: (id) => apiCall(`/api/supplier-invoices/${id}/mark-paid`, { method: 'PATCH' }),
  addPayment: (invoiceId, data) => apiCall(`/api/supplier-invoices/${invoiceId}/payment`, { method: 'POST', body: JSON.stringify(data) }),
  createDebitNote: (data) => apiCall('/api/supplier-invoices', { method: 'POST', body: JSON.stringify({ ...data, invoice_type: 'debit_note' }) }),
};

// Recurring Expenses API
export const recurringExpensesApi = {
  getAll: () => apiCall('/api/recurring-expenses'),
  getById: (id) => apiCall(`/api/recurring-expenses/${id}`),
  create: (data) => apiCall('/api/recurring-expenses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api/recurring-expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api/recurring-expenses/${id}`, { method: 'DELETE' }),
  toggle: (id) => apiCall(`/api/recurring-expenses/${id}/toggle`, { method: 'PATCH' }),
  generate: () => apiCall('/api/recurring-expenses/generate', { method: 'POST' }),
};

// Organizations API (Multi-tenant)
export const organizationsApi = {
  // Get current user's organizations
  getMyOrganizations: () => apiCall('/api/organizations'),
  
  // Get specific organization
  getById: (id) => apiCall(`/api/organizations/${id}`),
  
  // Create new organization
  create: (data) => apiCall('/api/organizations', { method: 'POST', body: JSON.stringify(data) }),
  
  // Update organization
  update: (id, data) => apiCall(`/api/organizations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  
  // Set default organization
  setDefault: (id) => apiCall(`/api/organizations/${id}/set-default`, { method: 'POST' }),
  
  // Get organization members
  getMembers: (orgId) => apiCall(`/api/organizations/${orgId}/members`),
  
  // Invite user to organization
  invite: (orgId, data) => apiCall(`/api/organizations/${orgId}/invite`, { method: 'POST', body: JSON.stringify(data) }),
  
  // Get invitation details
  getInvitation: (token) => apiCall(`/api/organizations/invite/${token}`),
  
  // Accept invitation
  acceptInvitation: (token) => apiCall(`/api/organizations/accept-invite/${token}`, { method: 'POST' }),
  
  // Get available roles
  getRoles: () => apiCall('/api/organizations/meta/roles'),
};

